"use client";

import { Button } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle, PlusIcon, RefreshCw, ScanBarcodeIcon, Search } from "lucide-react";

export type ShopPosScanErrorDialogProps = {
  open: boolean;
  onClose: () => void;
  failedBarcode: string;
  shopId: string | undefined;
  shopIdShort: string;
  canAddProduct: boolean;
  onRetryScan: () => void;
  onAddProduct: () => void;
};

/** Dialog ເມື່ອສະແກນບໍ່ພົບສິນຄ້າ */
export function ShopPosScanErrorDialog({
  open,
  onClose,
  failedBarcode,
  shopId,
  shopIdShort,
  canAddProduct,
  onRetryScan,
  onAddProduct,
}: ShopPosScanErrorDialogProps) {
  const nav = useNavigate();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-foreground/40 backdrop-blur-[2px]"
        aria-label="ປິດ"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-scan-error-title"
        className="relative z-10 w-full max-w-md overflow-hidden rounded-xl border border-border bg-card shadow-xl"
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      >
        <div
          className="absolute top-0 bottom-0 left-0 w-1 bg-destructive"
          aria-hidden
        />
        <div className="p-5 pl-6">
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <AlertTriangle className="size-5" />
            </div>
            <div>
              <h2
                id="pos-scan-error-title"
                className="font-bold font-heading text-base text-card-foreground uppercase tracking-tight"
              >
                ສະແກບໍ່ສຳເລັດ
              </h2>
              <p className="mt-1 text-muted-foreground text-sm leading-relaxed">
                ລະບົບບໍ່ພົບສິນຄ້ານີ້ໃນຖານຂໍ້ມູນສິນຄ້າຂອງຮ້ານນີ້
              </p>
            </div>
          </div>

          <div className="mt-4">
            <p className="mb-1 font-bold text-[10px] text-destructive uppercase tracking-wider">
              ລະຫັດທີ່ສະແກນ
            </p>
            <div className="flex items-center gap-2 rounded-lg border-2 border-destructive/40 bg-destructive/5 px-3 py-2.5">
              <ScanBarcodeIcon className="size-5 shrink-0 text-destructive" />
              <span className="min-w-0 flex-1 break-all font-mono text-card-foreground text-sm">
                {failedBarcode}
              </span>
            </div>
            <p className="mt-2 flex items-start gap-2 text-destructive text-xs">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
              <span>
                ບໍ່ພົບ barcode ນີ້ໃນຮ້ານ
                {shopId ? ` (${shopIdShort}…)` : ""}. ກວດຄືນ ຫຼື ຄົ້ນໃນລາຍການສິນຄ້າ
              </span>
            </p>
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              className="h-10 flex-1 gap-2"
              onClick={() => {
                onClose();
                onRetryScan();
              }}
            >
              <RefreshCw className="size-4" />
              ລອງສະແກນອີກ
            </Button>
            {shopId ? (
              <Button
                type="button"
                variant="secondary"
                className="h-10 flex-1 gap-2 bg-foreground text-background hover:bg-foreground/90"
                onClick={() => {
                  onClose();
                  nav({
                    to: "/app/shops/$shopId/catalog",
                    params: { shopId },
                  });
                }}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <Search className="size-4 shrink-0" aria-hidden />
                  ຄົ້ນດ້ວຍມື
                </span>
              </Button>
            ) : null}
            {shopId && canAddProduct ? (
              <Button
                type="button"
                variant="default"
                className="h-10 flex-1 gap-2"
                onClick={() => {
                  onClose();
                  onAddProduct();
                }}
              >
                <PlusIcon className="size-4 shrink-0" aria-hidden />
                ເພີ່ມສິນຄ້າໃໝ່
              </Button>
            ) : null}
          </div>
          <p className="mt-4 font-mono text-[10px] text-muted-foreground">
            LOCAL_SHOP_LOOKUP · {new Date().toISOString()}
          </p>
        </div>
      </div>
    </div>
  );
}
